import React, { Fragment, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { signout, isAuthenticated } from '../auth';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  useMediaQuery,
  useTheme,
  Divider,
  Typography,
  ListItemIcon
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import InfoIcon from '@mui/icons-material/Info';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BusinessIcon from '@mui/icons-material/Business';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import DescriptionIcon from '@mui/icons-material/Description';
import CampaignIcon from '@mui/icons-material/Campaign';

const isActive = (history, path) => {
  if (history.location.pathname === path) {
    return {
      color: '#FF6600',
      borderBottom: '2px solid #FF6600'
    };
  }
  return { color: 'white' };
};

const MenuComponent = ({ history }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [eventsAnchor, setEventsAnchor] = useState(null);
  const [aboutAnchor, setAboutAnchor] = useState(null);
  const [resourcesAnchor, setResourcesAnchor] = useState(null);

  const handleEventsClick = (event) => {
    setEventsAnchor(event.currentTarget);
  };

  const handleEventsClose = () => {
    setEventsAnchor(null);
  };

  const handleAboutClick = (event) => {
    setAboutAnchor(event.currentTarget);
  };

  const handleAboutClose = () => {
    setAboutAnchor(null);
  };

  const handleResourcesClick = (event) => {
    setResourcesAnchor(event.currentTarget);
  };

  const handleResourcesClose = () => {
    setResourcesAnchor(null);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleSignout = () => {
    signout(() => history.push('/'));
  };

  // Desktop navigation for unauthenticated users
  const renderPublicDesktopNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button
        component={Link}
        to="/"
        startIcon={<HomeIcon />}
        sx={{
          ...isActive(history, '/'),
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Home
      </Button>

      <Button
        onClick={handleEventsClick}
        endIcon={<ExpandMoreIcon />}
        startIcon={<EventIcon />}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Events
      </Button>
      <Menu
        anchorEl={eventsAnchor}
        open={Boolean(eventsAnchor)}
        onClose={handleEventsClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem component={Link} to="/publicNewOrleans" onClick={handleEventsClose}>
          <ListItemIcon><EventIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="2027 Conference" secondary="New Orleans" />
        </MenuItem>
        <MenuItem component={Link} to="/callForPresentations2027" onClick={handleEventsClose}>
          <ListItemIcon><CampaignIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Call for Presentations" secondary="2027 Conference" />
        </MenuItem>
        <MenuItem component={Link} to="/past-events" onClick={handleEventsClose}>
          <ListItemIcon><EmojiEventsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Past Events</ListItemText>
        </MenuItem>
      </Menu>

      <Button
        onClick={handleAboutClick}
        endIcon={<ExpandMoreIcon />}
        startIcon={<InfoIcon />}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        About
      </Button>
      <Menu
        anchorEl={aboutAnchor}
        open={Boolean(aboutAnchor)}
        onClose={handleAboutClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem component={Link} to="/memberLogos" onClick={handleAboutClose}>
          <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Members</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/about" onClick={handleAboutClose}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Team</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/awards" onClick={handleAboutClose}>
          <ListItemIcon><EmojiEventsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Awards</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/podcast" onClick={handleAboutClose}>
          <ListItemIcon><PodcastsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>PowerUp Podcast</ListItemText>
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

      <Button
        component={Link}
        to="/signin"
        startIcon={<LoginIcon />}
        sx={{
          ...isActive(history, '/signin'),
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Sign In
      </Button>
      <Button
        component={Link}
        to="/signup"
        startIcon={<PersonAddIcon />}
        variant="outlined"
        sx={{
          color: 'white',
          borderColor: 'white',
          '&:hover': {
            borderColor: '#FF6600',
            backgroundColor: '#FF6600',
            color: 'white'
          }
        }}
      >
        Sign Up
      </Button>
    </Box>
  );

  // Desktop navigation for authenticated users
  const renderAuthDesktopNav = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto' }}>
      <Button
        component={Link}
        to="/usershome"
        startIcon={<HomeIcon />}
        sx={{
          ...isActive(history, '/usershome'),
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Home
      </Button>

      <Button
        component={Link}
        to="/grouplist"
        startIcon={<GroupsIcon />}
        sx={{
          ...isActive(history, '/grouplist'),
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Committees
      </Button>

      <Button
        onClick={handleEventsClick}
        endIcon={<ExpandMoreIcon />}
        startIcon={<EventIcon />}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Events
      </Button>
      <Menu
        anchorEl={eventsAnchor}
        open={Boolean(eventsAnchor)}
        onClose={handleEventsClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 220 }
        }}
      >
        <MenuItem component={Link} to="/allevents" onClick={handleEventsClose}>
          <ListItemIcon><CalendarMonthIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Committee Events</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem component={Link} to="/publicNewOrleans" onClick={handleEventsClose}>
          <ListItemIcon><EventIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="2027 Conference" secondary="New Orleans" />
        </MenuItem>
        <MenuItem component={Link} to="/callForPresentations2027" onClick={handleEventsClose}>
          <ListItemIcon><CampaignIcon fontSize="small" color="primary" /></ListItemIcon>
          <ListItemText primary="Call for Presentations" secondary="2027 Conference" />
        </MenuItem>
        <MenuItem component={Link} to="/past-events" onClick={handleEventsClose}>
          <ListItemIcon><EmojiEventsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Past Events</ListItemText>
        </MenuItem>
      </Menu>

      <Button
        onClick={handleResourcesClick}
        endIcon={<ExpandMoreIcon />}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        Resources
      </Button>
      <Menu
        anchorEl={resourcesAnchor}
        open={Boolean(resourcesAnchor)}
        onClose={handleResourcesClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem component={Link} to="/findpeople" onClick={handleResourcesClose}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Members</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/podcast" onClick={handleResourcesClose}>
          <ListItemIcon><PodcastsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Podcast</ListItemText>
        </MenuItem>

      </Menu>

      <Button
        onClick={handleAboutClick}
        endIcon={<ExpandMoreIcon />}
        startIcon={<InfoIcon />}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
      >
        About
      </Button>
      <Menu
        anchorEl={aboutAnchor}
        open={Boolean(aboutAnchor)}
        onClose={handleAboutClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1, minWidth: 200 }
        }}
      >
        <MenuItem component={Link} to="/memberLogos" onClick={handleAboutClose}>
          <ListItemIcon><BusinessIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Members</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/about" onClick={handleAboutClose}>
          <ListItemIcon><PeopleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Team</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/charter" onClick={handleAboutClose}>
          <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Charter</ListItemText>
        </MenuItem>
        <MenuItem component={Link} to="/awards" onClick={handleAboutClose}>
          <ListItemIcon><EmojiEventsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Our Awards</ListItemText>
        </MenuItem>
      </Menu>

      <Divider orientation="vertical" flexItem sx={{ mx: 1, backgroundColor: 'rgba(255, 255, 255, 0.2)' }} />

      <IconButton
        component={Link}
        to={`/user/${isAuthenticated() && isAuthenticated().userId}`}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
        title={`${isAuthenticated().name}'s profile`}
      >
        <AccountCircleIcon />
      </IconButton>

      <IconButton
        onClick={handleSignout}
        sx={{
          color: 'white',
          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
        }}
        title="Sign Out"
      >
        <LogoutIcon />
      </IconButton>
    </Box>
  );

  // Mobile navigation drawer
  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={closeMobileMenu}
      PaperProps={{
        sx: {
          backgroundColor: '#f8f9fa',
          width: 280,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box role="presentation" sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        {/* Drawer Header */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#003E7E',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            <Box component="span" sx={{ color: '#FF6600' }}>SDS</Box>
            <Box component="span">IC</Box>
          </Typography>
          <IconButton onClick={closeMobileMenu} sx={{ color: 'white' }}>
            <MenuIcon />
          </IconButton>
        </Box>
        <List sx={{ flex: 1, overflowY: 'auto', pb: 2 }}>
          {!isAuthenticated() ? (
            <Fragment>
              <ListItem button component={Link} to="/" onClick={closeMobileMenu}>
                <ListItemText primary="Home" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Events" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItem>
              <ListItem button component={Link} to="/publicNewOrleans" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="2027 Conference" />
              </ListItem>
              <ListItem button component={Link} to="/callForPresentations2027" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Call for Presentations" />
              </ListItem>
              <ListItem button component={Link} to="/past-events" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Past Events" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="About Us" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItem>
              <ListItem button component={Link} to="/memberLogos" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Members" />
              </ListItem>
              <ListItem button component={Link} to="/about" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Team" />
              </ListItem>
              <ListItem button component={Link} to="/awards" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Awards" />
              </ListItem>
              <ListItem button component={Link} to="/sdsicmerch" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Merchandise" />
              </ListItem>
              <ListItem button component={Link} to="/podcast" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="PowerUp Podcast" />
              </ListItem>
              <Divider />
              <ListItem button component={Link} to="/signin" onClick={closeMobileMenu}>
                <ListItemText primary="Sign In" />
              </ListItem>
              <ListItem button component={Link} to="/signup" onClick={closeMobileMenu}>
                <ListItemText primary="Sign Up" />
              </ListItem>
            </Fragment>
          ) : (
            <Fragment>
              <ListItem button component={Link} to="/usershome" onClick={closeMobileMenu}>
                <ListItemText primary="Home" />
              </ListItem>
              <ListItem button component={Link} to="/grouplist" onClick={closeMobileMenu}>
                <ListItemText primary="Committees" />
              </ListItem>
              <ListItem button component={Link} to="/allevents" onClick={closeMobileMenu}>
                <ListItemText primary="Committee Events" />
              </ListItem>
              <ListItem button component={Link} to="/findpeople" onClick={closeMobileMenu}>
                <ListItemText primary="Members" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="Events" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItem>
              <ListItem button component={Link} to="/publicNewOrleans" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="2027 Conference — New Orleans" />
              </ListItem>
              <ListItem button component={Link} to="/callForPresentations2027" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Call for Presentations" />
              </ListItem>
              <ListItem button component={Link} to="/past-events" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Past Events" />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText primary="About Us" primaryTypographyProps={{ fontWeight: 'bold' }} />
              </ListItem>
              <ListItem button component={Link} to="/memberLogos" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Members" />
              </ListItem>
              <ListItem button component={Link} to="/about" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Team" />
              </ListItem>
              <ListItem button component={Link} to="/charter" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText>Charter</ListItemText>
              </ListItem>
              <ListItem button component={Link} to="/awards" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Our Awards" />
              </ListItem>
              <ListItem button component={Link} to="/sdsicmerch" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="Merchandise" />
              </ListItem>
              <ListItem button component={Link} to="/podcast" onClick={closeMobileMenu} sx={{ pl: 4 }}>
                <ListItemText primary="PowerUp Podcast" />
              </ListItem>
              <Divider />
              <ListItem
                button
                component={Link}
                to={`/user/${isAuthenticated() && isAuthenticated().userId}`}
                onClick={closeMobileMenu}
              >
                <ListItemText primary={`${isAuthenticated().name}'s profile`} />
              </ListItem>
              <ListItem button onClick={() => { handleSignout(); closeMobileMenu(); }}>
                <ListItemText primary="Sign Out" />
              </ListItem>
            </Fragment>
          )}
        </List>

        {/* Drawer Footer */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#e9ecef',
            borderTop: '1px solid #dee2e6',
            flexShrink: 0
          }}
        >
          <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
            © {new Date().getFullYear()} SDSIC
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: '#003E7E',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Toolbar sx={{ minHeight: { xs: 64, sm: 70 } }}>
        {/* Logo/Brand */}
        <Box
          component={Link}
          to={isAuthenticated() ? "/usershome" : "/"}
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            mr: 3
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'white',
              letterSpacing: '-0.5px',
              fontSize: { xs: '1.1rem', sm: '1.3rem' }
            }}
          >
            <Box component="span" sx={{ color: '#FF6600' }}>SDS</Box>
            <Box component="span">IC</Box>
          </Typography>
        </Box>

        {/* Mobile menu button */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleMobileMenu}
            sx={{
              ml: 'auto',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Desktop navigation */}
        {!isMobile && !isAuthenticated() && renderPublicDesktopNav()}
        {!isMobile && isAuthenticated() && renderAuthDesktopNav()}

        {/* Mobile drawer */}
        {renderMobileDrawer()}
      </Toolbar>
    </AppBar>
  );
};

export default withRouter(MenuComponent);
